import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Alert, Checkbox, Space, Divider, message, Tag } from 'antd';
import { FilePdfOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Fornecedor } from '../types/dashboard/fornecedores.types';

interface RelatorioFiltrosModalProps {
  visible: boolean;
  onCancel: () => void;
  onGenerate: (filtros: any) => void;
  clienteId: string;
  clienteNome: string;
  fornecedoresList: Fornecedor[];
  filtrosAtuais: {
    search?: string;
    status?: string[];
  };
  tipo: 'dashboard' | 'fornecedor';
  fornecedorId?: string;
  fornecedorPredefinido?: string;
}

const RelatorioFiltrosModal: React.FC<RelatorioFiltrosModalProps> = ({
  visible,
  onCancel,
  onGenerate,
  clienteId,
  clienteNome,
  fornecedoresList,
  filtrosAtuais,
  tipo,
  fornecedorId,
  fornecedorPredefinido
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tipoVisualizacao, setTipoVisualizacao] = useState<'consolidada' | 'detalhada'>('consolidada');
  const [filtros, setFiltros] = useState({
    fornecedorId: '',
    opcoes: {
      incluirEstatisticas: true,
      incluirPlanosDeAcao: true,
      incluirMaturidade: true,
      incluirTabela: true,
      incluirInfoFornecedor: true,
      incluirQuestionarios: true,
      incluirTimeline: true,
      incluirTentativasEnvio: true,
      incluirDetalhesCompletos: false,
      tentativaFiltro: 'todas' as 'todas' | '1' | '2' | '3'
    }
  });

  useEffect(() => {
    if (visible) {
      const fornecedorParaSelecionar = fornecedorPredefinido || fornecedorId || '';
      
      setFiltros({
        fornecedorId: fornecedorParaSelecionar,
        opcoes: {
          incluirEstatisticas: true,
          incluirMaturidade: true,
          incluirPlanosDeAcao: true,
          incluirTabela: true,
          incluirInfoFornecedor: true,
          incluirQuestionarios: true,
          incluirTimeline: true,
          incluirTentativasEnvio: true,
          incluirDetalhesCompletos: false,
          tentativaFiltro: 'todas'
        }
      });
      
      if (fornecedorParaSelecionar && tipo === 'dashboard') {
        setTipoVisualizacao('detalhada');
      } else {
        setTipoVisualizacao(tipo === 'fornecedor' ? 'detalhada' : 'consolidada');
      }
    }
  }, [visible, fornecedorId, tipo, fornecedorPredefinido]);

  const handleGenerate = () => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const filtrosPreparados = {
        ...filtros,
        tipoVisualizacao,
        nomeCliente: clienteNome,
        opcoes: {
          incluirEstatisticas: filtros.opcoes.incluirEstatisticas,
          incluirMaturidade: filtros.opcoes.incluirMaturidade,
          incluirPlanosDeAcao: filtros.opcoes.incluirPlanosDeAcao,
          incluirTabela: filtros.opcoes.incluirTabela,
          incluirQuestionarios: filtros.opcoes.incluirQuestionarios,
          incluirInfoFornecedor: filtros.opcoes.incluirInfoFornecedor,
          incluirTimeline: filtros.opcoes.incluirTimeline,
          incluirTentativasEnvio: filtros.opcoes.incluirTentativasEnvio,
          incluirDetalhesCompletos: filtros.opcoes.incluirDetalhesCompletos,
          tentativaFiltro: filtros.opcoes.tentativaFiltro
        }
      };
      
      if (tipoVisualizacao === 'detalhada' && !filtros.fornecedorId && fornecedorId) {
        filtrosPreparados.fornecedorId = fornecedorId;
      }
      
      if (tipoVisualizacao === 'detalhada' && !filtrosPreparados.fornecedorId) {
        message.error('Selecione um fornecedor para gerar o relatório detalhado');
        setLoading(false);
        return;
      }
      
      onGenerate(filtrosPreparados);
      
    } catch (error) {
      message.error('Falha ao preparar os filtros para o relatório.');
    } finally {
      setLoading(false);
    }
  };

  const renderTipoVisualizacao = () => {
    return (
      <Form.Item label="Tipo de Visualização">
        <div style={{ padding: '8px 0' }}>
          <Tag 
            color={tipoVisualizacao === 'consolidada' ? 'purple' : 'purple'} 
            style={{ fontSize: '14px', padding: '4px 12px' }}
          >
            {tipoVisualizacao === 'consolidada' ? 'Visualização Consolidada' : 'Informações Gerais'}
          </Tag>
          <span style={{ fontSize: '14px', marginLeft: '10px', color: '#888' }}>
            <InfoCircleOutlined /> Tipo de relatório selecionado
          </span>
          
          {fornecedorPredefinido && (
            <div style={{ marginTop: '8px' }}>
              <Button 
                size="small" 
                type="link" 
                onClick={() => {
                  const novoTipo = tipoVisualizacao === 'consolidada' ? 'detalhada' : 'consolidada';
                  setTipoVisualizacao(novoTipo);
                }}
              >
                Alternar para {tipoVisualizacao === 'consolidada' ? 'Informações Gerais' : 'Visualização Consolidada'}
              </Button>
            </div>
          )}
        </div>
      </Form.Item>
    );
  };

  return (
    <Modal
      title={tipo === 'fornecedor' ? "Relatório de Fornecedores" : "Relatório do Cliente"}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading} 
          onClick={handleGenerate}
          icon={<FilePdfOutlined />}
        >
          Gerar PDF
        </Button>
      ]}
      width={650}
    >
      <Form layout="vertical">
        {renderTipoVisualizacao()}
        
        {tipoVisualizacao === 'detalhada' && (
          <Form.Item 
            label="Selecione um fornecedor" 
            required={tipoVisualizacao === 'detalhada'}
            rules={[{ required: tipoVisualizacao === 'detalhada', message: 'Por favor selecione um fornecedor' }]}
          >
            <Select
              placeholder="Selecione um fornecedor"
              style={{ width: '100%' }}
              value={filtros.fornecedorId || undefined}
              onChange={(value) => {
                setFiltros(prev => ({ ...prev, fornecedorId: value }));
              }}
              showSearch
              optionFilterProp="children"
              allowClear={tipoVisualizacao !== 'detalhada'}
            >
              {fornecedoresList.map(fornecedor => (
                <Select.Option key={fornecedor.id} value={fornecedor.id}>
                  {fornecedor.razaoSocial || "Fornecedor sem nome"}
                </Select.Option>
              ))}
            </Select>
            <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
              {tipoVisualizacao === 'detalhada' 
                ? "Obrigatório para relatório detalhado"
                : "Deixe em branco para incluir todos os fornecedores"}
              {fornecedorPredefinido && (
                <div style={{ marginTop: 4, color: '#1890ff' }}>
                </div>
              )}
            </div>
          </Form.Item>
        )}
        
        <Divider>Conteúdo do Relatório</Divider>
        
        <Form.Item>
          <Space direction="vertical">
            {tipoVisualizacao === 'consolidada' && (
              <>
                <Checkbox 
                  checked={filtros.opcoes.incluirEstatisticas}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirEstatisticas: e.target.checked }
                  }))}
                >
                  Incluir estatísticas gerais
                </Checkbox>
                
                <Checkbox 
                  checked={filtros.opcoes.incluirPlanosDeAcao}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirPlanosDeAcao: e.target.checked }
                  }))}
                >
                  Incluir planos de ação
                </Checkbox>

                <Checkbox 
                  checked={filtros.opcoes.incluirMaturidade}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirMaturidade: e.target.checked }
                  }))}
                >
                  Incluir dados de maturidade
                </Checkbox>
                
                <Checkbox 
                  checked={filtros.opcoes.incluirTabela}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirTabela: e.target.checked }
                  }))}
                >
                  Incluir tabela de fornecedores
                </Checkbox>
              </>
            )}
            
            {tipoVisualizacao === 'detalhada' && (
              <>
                <Checkbox 
                  checked={filtros.opcoes.incluirInfoFornecedor}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirInfoFornecedor: e.target.checked }
                  }))}
                >
                  Incluir informações detalhadas do fornecedor
                </Checkbox>

                <Checkbox 
                  checked={filtros.opcoes.incluirPlanosDeAcao}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirPlanosDeAcao: e.target.checked }
                  }))}
                >
                  Incluir planos de ação
                </Checkbox>

                <Checkbox 
                  checked={filtros.opcoes.incluirMaturidade}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirMaturidade: e.target.checked }
                  }))}
                >
                  Incluir dados de maturidade
                </Checkbox>
                
                <Checkbox 
                  checked={filtros.opcoes.incluirQuestionarios}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirQuestionarios: e.target.checked }
                  }))}
                >
                  Incluir histórico de questionários
                </Checkbox>
                
                <Checkbox 
                  checked={filtros.opcoes.incluirTimeline}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirTimeline: e.target.checked }
                  }))}
                >
                  Incluir timeline da ação
                </Checkbox>
              </>
            )}

            <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
              Tentativas de Envio
            </Divider>
            
            <Checkbox 
              checked={filtros.opcoes.incluirTentativasEnvio}
              onChange={(e) => setFiltros(prev => ({
                ...prev,
                opcoes: { ...prev.opcoes, incluirTentativasEnvio: e.target.checked }
              }))}
            >
              Incluir estatísticas de tentativas de envio
            </Checkbox>
            
            {filtros.opcoes.incluirTentativasEnvio && (
              <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                <Checkbox 
                  checked={filtros.opcoes.incluirDetalhesCompletos}
                  onChange={(e) => setFiltros(prev => ({
                    ...prev,
                    opcoes: { ...prev.opcoes, incluirDetalhesCompletos: e.target.checked }
                  }))}
                >
                  Incluir tabela detalhada de todas as tentativas
                </Checkbox>
                
                {filtros.opcoes.incluirDetalhesCompletos && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      Filtrar por tentativa:
                    </span>
                    <Select
                      size="small"
                      value={filtros.opcoes.tentativaFiltro}
                      onChange={(value) => setFiltros(prev => ({
                        ...prev,
                        opcoes: { ...prev.opcoes, tentativaFiltro: value }
                      }))}
                      style={{ width: '120px', marginLeft: '8px' }}
                    >
                      <Select.Option value="todas">Todas</Select.Option>
                      <Select.Option value="1">1ª Tentativa</Select.Option>
                      <Select.Option value="2">2ª Tentativa</Select.Option>
                      <Select.Option value="3">3ª Tentativa</Select.Option>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </Space>
        </Form.Item>
        
        <Alert
          message="Observação"
          description={
            <div>
              {tipoVisualizacao === 'consolidada' 
                ? "O relatório consolidado apresentará uma visão geral de todos os fornecedores." 
                : "O relatório detalhado apresentará informações específicas do fornecedor selecionado."
              }
              {filtros.opcoes.incluirTentativasEnvio && (
                <div style={{ marginTop: '8px' }}>
                  <strong>Tentativas de Envio:</strong> Incluirá estatísticas sobre as tentativas de envio de questionários
                  {filtros.opcoes.incluirDetalhesCompletos && 
                    ` com detalhamento completo${filtros.opcoes.tentativaFiltro !== 'todas' ? 
                      ` filtrado pela ${filtros.opcoes.tentativaFiltro}ª tentativa` : ''}.`
                  }
                </div>
              )}
            </div>
          }
          type="info"
          showIcon
        />
        
        <div style={{ marginTop: 8, fontSize: 14, color: '#888' }}>
          Cliente: {clienteNome || "Nome não disponível"}
        </div>
      </Form>
    </Modal>
  );
};

export default RelatorioFiltrosModal;